import { useEffect } from 'react';
import { useFormikContext } from 'formik';
import type { CreateCargoFormValues } from '@/types/cargoList.types';

interface SelectDefaultsSeederProps {
  status?: string | number;
  certainty?: string | number;
  privacyLevel?: string | number;
}

/**
 * Seeds the three reference-data selects once their options arrive.
 *
 * This replaces a `key` on `<Formik>` built from the option-list lengths. That
 * key changed when the (batched) reference-data fetch resolved, remounting the
 * whole form and silently discarding anything the user had already typed - and
 * since the modal fetches on open, it happened on every single open.
 *
 * Each field is filled only while still blank, so a value the user has already
 * chosen is never overwritten and the seed never runs twice. Seeding does not
 * validate: these are defaults the user has not interacted with yet.
 */
export function SelectDefaultsSeeder({
  status,
  certainty,
  privacyLevel,
}: SelectDefaultsSeederProps) {
  const { values, setValues } = useFormikContext<CreateCargoFormValues>();

  useEffect(() => {
    const patch: Partial<CreateCargoFormValues> = {};

    if (values.status === '' && status !== undefined) patch.status = status;
    if (values.certainty === '' && certainty !== undefined) patch.certainty = certainty;
    if (values.privacyLevel === '' && privacyLevel !== undefined) patch.privacyLevel = privacyLevel;

    if (Object.keys(patch).length > 0) {
      setValues((previous) => ({ ...previous, ...patch }), false);
    }
  }, [
    status,
    certainty,
    privacyLevel,
    values.status,
    values.certainty,
    values.privacyLevel,
    setValues,
  ]);

  return null;
}
